import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getQuotes } from '../../clients/quote_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { getUserSelectList } from '../../clients/user_client';
import globalSupplierSelectCache from '../../config_and_helpers/supplier_cache';
import QuoteModal from '../../components/Quote/QuoteModal';
import InfiniteScroll from 'react-infinite-scroller';
import { showToast } from '../../config_and_helpers/helpers';
import QuoteTable from '../../components/Quote/QuoteTable';
import { Spinner } from 'react-bootstrap';

import GenericSearchFilter from '../Generic/GenericSearchFilter';
import DropdownSelect from '../Generic/DropdownSelect';
import {
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  normalizeListSearchParam,
  resetPaginationForPartialSearch,
  shouldBootstrapInfiniteScrollLoad,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

export const QuoteContext = React.createContext();

const normalizeParam = normalizeListSearchParam;

const QuotesList = ({
  activeTab,
  supplierSelectList: propSupplierSelectList,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null); // Used to cancel previous requests
  const hasMoreRef = useRef(true); // Ref to track hasMore state across renders
  const isFetchingRef = useRef(false); // Synchronous in-flight guard for race-free pagination

  // Core state variables
  const [baseQuotes, setBaseQuotes] = useState([]); // complete dataset
  const [viewQuotes, setViewQuotes] = useState([]); // filtered/subset of baseQuotes
  const [supplier, setSupplier] = useState(''); // supplier filter
  const [localSupplierSelectList, setLocalSupplierSelectList] = useState([]); // supplier select list for search filter
  const [nextPageUrl, setNextPageUrl] = useState(null); // for pagination
  const [hasMore, setHasMore] = useState(true); // infinite scroll trigger
  const [searchInput, setSearchInput] = useState(''); // search term
  const [isLoading, setIsLoading] = useState(false); // loading state
  const [isSearching, setIsSearching] = useState(false); // Search-specific loading state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedUser, setSelectedUser] = useState('');
  const [userSelectList, setUserSelectList] = useState([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clean up debounce timeout and requests
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
      isFetchingRef.current = false;
    };
  }, []);

  // Fetch supplier select list on component mount
  useEffect(() => {
    // Use prop supplier list if provided
    if (propSupplierSelectList && propSupplierSelectList.length > 0) {
      setLocalSupplierSelectList(propSupplierSelectList);
      return;
    }

    // Check if cache exists and hasn't been invalidated
    const cacheIsValid = globalSupplierSelectCache.data && 
      (!globalSupplierSelectCache.invalidatedAt || 
       !globalSupplierSelectCache.timestamp ||
       globalSupplierSelectCache.timestamp > globalSupplierSelectCache.invalidatedAt);

    // Use global cache if available and valid
    if (cacheIsValid) {
      setLocalSupplierSelectList(globalSupplierSelectCache.data);
      return;
    }

    // If already fetching, wait for the promise
    if (
      globalSupplierSelectCache.isFetching &&
      globalSupplierSelectCache.promise
    ) {
      globalSupplierSelectCache.promise.then(() => {
        if (globalSupplierSelectCache.data) {
          setLocalSupplierSelectList(globalSupplierSelectCache.data);
        }
      });
      return;
    }

    // Fetch and cache the data
    globalSupplierSelectCache.isFetching = true;
    globalSupplierSelectCache.promise = getSupplierSelectList(
      token,
      data => {
        globalSupplierSelectCache.data = data;
        globalSupplierSelectCache.timestamp = Date.now();
        globalSupplierSelectCache.isFetching = false;
        setLocalSupplierSelectList(data);
      },
      'quotes'
    );

    globalSupplierSelectCache.promise.then(() => {
      globalSupplierSelectCache.isFetching = false;
    });
  }, [token, propSupplierSelectList]);

  useEffect(() => {
    getUserSelectList(token, setUserSelectList);
  }, [token]);

  /**
   * Fetch quotes with search, pagination, supplier filter, and optional user filter.
   * Cleared user dropdown (no `user_id`) returns quotes for all users.
   */
  const fetchQuotes = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        supplierFilter = '',
        userFilter = '',
        sortByValue = 'created_at',
        sortDirectionValue = 'desc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        nextPage,
        supplierFilter,
        userFilter,
        fulfilledFilter: activeTab === 'fulfilled',
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      // Prevent fetching if we know there are no more pages (unless it's a new search)
      if (!newSearch && !hasMoreRef.current && nextPage) {
        return;
      }

      // Pagination must not start a first-page fetch (nextPage null) or race an in-flight request.
      if (!newSearch) {
        if (!nextPage || isLoading || isFetchingRef.current) {
          return;
        }
      }

      // Supersede any in-flight request only when we are about to start a replacement fetch.
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
      if (newSearch) {
        isFetchingRef.current = false;
      } else if (isFetchingRef.current) {
        return;
      }

      // Additional safety check - prevent any request if hasMore is false
      if (!hasMoreRef.current && nextPage) {
        return;
      }

      // Set loading state
      isFetchingRef.current = true;
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        // Clear existing quotes for new search
        setBaseQuotes([]);
        setViewQuotes([]);
        // Reset pagination state for new search
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      const options = {
        searchInput: searchValue,
        nextPage: nextPage,
        fulfilledFilter: activeTab === 'fulfilled',
        supplierFilter: supplierFilter,
        userFilter: userFilter,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      };

      logListSearchEvent('quotes', 'request', {
        trigger,
        params,
      });

      getQuotes(token, setBaseQuotes, options)
        .then(response => {
          if (!isMountedRef.current) return;
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          const responseMeta = {
            success: response && response.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            // Clean the nextPage URL to prevent parameter accumulation
            let cleanNextPage = response.nextPage;
            if (cleanNextPage && !response.reachedEnd) {
              try {
                const url = new URL(cleanNextPage);
                // Keep page_num parameter and current search value, remove accumulated search parameters
                const cleanUrl = new URL(url.origin + url.pathname);
                cleanUrl.searchParams.set(
                  'page_num',
                  url.searchParams.get('page_num')
                );
                // Add current search value if it exists
                if (searchValue) {
                  cleanUrl.searchParams.set('search', searchValue);
                }
                // Add supplier filter if it exists
                if (supplierFilter) {
                  cleanUrl.searchParams.set('supplier_filter', supplierFilter);
                }
                if (userFilter) {
                  cleanUrl.searchParams.set('user_id', userFilter);
                }
                // Add fulfilled filter if it exists
                if (activeTab === 'fulfilled') {
                  cleanUrl.searchParams.set('fulfilled_filter', 'True');
                }
                if (sortByValue) {
                  cleanUrl.searchParams.set('sort_by', sortByValue);
                }
                if (sortDirectionValue) {
                  cleanUrl.searchParams.set('sort_direction', sortDirectionValue);
                }
                cleanNextPage = cleanUrl.toString();
              } catch (error) {
                logListSearchError(
                  'quotes',
                  'next-page-url-clean',
                  error,
                  { params }
                );
              }
            }

            setNextPageUrl(response.reachedEnd ? null : cleanNextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            // Set hasMore to false to stop infinite scroll from trying to load more pages
            setHasMore(false);
            hasMoreRef.current = false;
            setNextPageUrl(null);

            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'quotes',
            response && response.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta,
            }
          );

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            isFetchingRef.current = false;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('quotes', trigger, error, { params });

          // Set hasMore to false to stop infinite scroll from trying to load more pages
          setHasMore(false);
          hasMoreRef.current = false;
          setNextPageUrl(null);

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          isFetchingRef.current = false;
        });
    },
    [token, activeTab, isLoading]
  );

  /**
   * Smart debounced search input handler with immediate UI feedback
   */
  const debouncedFetch = useCallback(
    (value, supplier, userFilter, sortByValue, sortDirectionValue) => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Immediate UI feedback: clear results and show searching state
      if (value.length >= 2) {
        setBaseQuotes([]);
        setViewQuotes([]);
        setIsSearching(true);
        // Reset pagination state for new search
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Only search if we have 2+ characters or empty string
      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchQuotes(
            {
              searchValue: value,
              nextPage: null,
              supplierFilter: supplier,
              userFilter,
              sortByValue,
              sortDirectionValue,
            },
            true
          ); // newSearch = true for search operations
        }, 300); // Reduced from 500ms to 300ms for better responsiveness
      } else if (isPartialListSearchQuery(value)) {
        // Partial query: clear results and kill stale pagination so InfiniteScroll
        // cannot loadMore against the previous next-page URL.
        setBaseQuotes([]);
        setViewQuotes([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchQuotes]
  );

  /**
   * Handles updates to the search input.
   */
  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedFetch(value, supplier, selectedUser, sortBy, sortDirection);
  };

  /**
   * Handles selection of a supplier filter.
   */
  const handleSupplierChange = value => {
    const supplierValue = normalizeParam(value);
    setSupplier(supplierValue);
    fetchQuotes(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplierValue,
        userFilter: selectedUser,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    ); // newSearch = true for filter changes
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchQuotes(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplier,
        userFilter: selectedUser,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };

  const handleUserChange = value => {
    const userValue = normalizeParam(value);
    setSelectedUser(userValue);
    fetchQuotes(
      {
        searchValue: searchInput,
        nextPage: null,
        supplierFilter: supplier,
        userFilter: userValue,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    );
  };

  /**
   * Clears the current search value.
   */
  const resetSearchValue = () => setSearchInput('');

  // Cleanup effect for debounce timeout and requests
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
      isFetchingRef.current = false;
    };
  }, []);

  return (
    <QuoteContext.Provider
      value={{
        quotes: viewQuotes.length ? viewQuotes : baseQuotes,
        setQuotes: viewQuotes.length ? setViewQuotes : setBaseQuotes,
      }}
    >
      <div>
        <div className="glass-toolbar">
        <GenericSearchFilter
          activeTab={activeTab}
          entityType="quotes"
          entity={supplier}
          searchInput={searchInput}
          onSearchInput={handleSearchInput}
          onEntityChange={handleSupplierChange}
          supplierSelectList={localSupplierSelectList}
          ModalComponent={
            isViewer ? null : (
            <QuoteModal
              onSuccessfulSubmit={() =>
                fetchQuotes(
                  {
                    searchValue: searchInput,
                    nextPage: null,
                    supplierFilter: supplier,
                    userFilter: selectedUser,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                )
              } // newSearch = true for refresh after modal submit
              clearSearchValue={resetSearchValue}
              supplierSelectList={localSupplierSelectList}
            />
            )
          }
          customActionButton={
            <div style={{ minWidth: '240px' }}>
              <DropdownSelect
                optionsList={userSelectList}
                label="User"
                selectedValue={findUserOption(userSelectList, selectedUser)}
                setSelectedValue={option =>
                  handleUserChange(option?.value || '')
                }
                disabledOptions={[]}
                clearable={true}
              />
            </div>
          }
        />
        </div>

        {(isLoading && !baseQuotes.length && !viewQuotes.length) ||
        isSearching ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
            {isSearching && searchInput.length >= 2 && (
              <div className="mt-2 text-muted">Searching quotes...</div>
            )}
          </div>
        ) : (
          <InfiniteScroll
            key={`${searchInput}-${supplier}-${selectedUser}-${sortBy}-${sortDirection}-${activeTab}-${hasMore}`} // Force reset when hasMore changes
            pageStart={0}
            initialLoad={hasMoreRef.current} // Only load initially if we have more pages
            loadMore={() => {
              if (
                isLoading ||
                isFetchingRef.current ||
                !hasMoreRef.current ||
                isPartialListSearchQuery(searchInput)
              ) {
                return;
              }

              const itemCount = (viewQuotes.length || baseQuotes.length) || 0;

              // InfiniteScroll initialLoad: fetch page 1 when the list is still empty.
              if (
                shouldBootstrapInfiniteScrollLoad({
                  nextPageUrl,
                  itemCount,
                })
              ) {
                fetchQuotes(
                  {
                    searchValue: searchInput,
                    nextPage: null,
                    supplierFilter: supplier,
                    userFilter: selectedUser,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                );
                return;
              }

              // Pagination requires a real next-page URL.
              if (!nextPageUrl) {
                return;
              }

              // Prevent using stale nextPage URLs when search/filter/sort has changed
              const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                search: searchInput,
                supplier_filter: supplier,
                user_id: selectedUser,
                fulfilled_filter: activeTab === 'fulfilled' ? 'True' : '',
                sort_by: sortBy,
                sort_direction: sortDirection,
              });
              if (mismatch === null) {
                logListSearchError(
                  'quotes',
                  'next-page-url-stale-check',
                  new Error('Failed to parse nextPageUrl'),
                  {
                    nextPageUrl,
                    searchInput,
                    supplier,
                    selectedUser,
                    activeTab,
                    sortBy,
                    sortDirection,
                  }
                );
                setNextPageUrl(null);
                setHasMore(false);
                hasMoreRef.current = false;
                return;
              }
              if (mismatch.mismatched) {
                logListSearchEvent('quotes', 'next-page-url-stale-mismatch', {
                  nextPageUrl,
                  expected: mismatch.expected,
                  actual: mismatch.actual,
                });
                setNextPageUrl(null);
                setHasMore(false);
                hasMoreRef.current = false;
                return;
              }

              fetchQuotes(
                {
                  searchValue: searchInput,
                  nextPage: nextPageUrl,
                  supplierFilter: supplier,
                  userFilter: selectedUser,
                  sortByValue: sortBy,
                  sortDirectionValue: sortDirection,
                },
                false
              ); // newSearch = false for pagination
            }}
            hasMore={hasMore && !isLoading}
            loader={
              <div key="quotes-loader" className="text-center py-2">
                <Spinner animation="border" size="sm" role="status" />
                <div className="mt-1">
                  <small className="text-muted">Loading more quotes...</small>
                </div>
              </div>
            }
            useWindow={true}
            threshold={INFINITE_SCROLL_THRESHOLD_PX}
          >
            {!baseQuotes.length ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <span
                    role="img"
                    aria-label="no results"
                    style={{ fontSize: '3rem' }}
                  >
                    📄
                  </span>
                </div>
                <h5 className="text-muted mb-2">No quotes found</h5>
                <p className="text-muted mb-3">
                  {searchInput.length >= 2
                    ? `No quotes match your search for "${searchInput}"`
                    : `No ${activeTab} quotes available`}
                </p>
                {searchInput.length >= 2 && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearchInput('');
                      fetchQuotes(
                        {
                          searchValue: '',
                          nextPage: null,
                          supplierFilter: supplier,
                          userFilter: selectedUser,
                          sortByValue: sortBy,
                          sortDirectionValue: sortDirection,
                        },
                        true
                      );
                    }}
                  >
                    <span role="img" aria-label="clear search">
                      🗑️
                    </span>{' '}
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="glass-table-wrap">
              <QuoteTable
                quoteList={viewQuotes.length ? viewQuotes : baseQuotes}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSortByChange}
                handleEdit={() =>
                  fetchQuotes(
                    {
                      searchValue: searchInput,
                      nextPage: null,
                      supplierFilter: supplier,
                      userFilter: selectedUser,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  )
                } // newSearch = true for refresh after edit
                handleDelete={() =>
                  fetchQuotes(
                    {
                      searchValue: searchInput,
                      nextPage: null,
                      supplierFilter: supplier,
                      userFilter: selectedUser,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  )
                } // newSearch = true for refresh after delete
                activeTab={activeTab}
                clearSearchValue={resetSearchValue}
                supplierSelectList={localSupplierSelectList}
              />
              </div>
            )}
          </InfiniteScroll>
        )}
      </div>
    </QuoteContext.Provider>
  );
};

export default QuotesList;
